import { findNewsSource } from '@tsr/shared-types';
import type { WallNewsFallbackItem } from './wallMedia';

/**
 * The wall's curated news-fallback set + the per-wall news POLICY gate (#362/#371).
 *
 * Extracted from `WallMediaZone` so the load-bearing gate — "GOC flanks NEVER
 * auto-rotate news; PEOC flanks do" — is a pure, unit-testable function rather
 * than an inline ternary in a heavy fetching component. Inverting/dropping the
 * gate now fails a test instead of silently letting GOC rotate news.
 */

/** Curated YouTube-live NEWS outlets that fill REMAINING empty wall slots once
 *  the broadcasting cameras are seated (#334/#362) — a blank then shows a live
 *  news tile instead of "Awaiting feed…". Ordered Canadian-relevance first
 *  (CBC · CTV · Global), then the persistent 24/7 international lives
 *  (Al Jazeera · France 24 · DW · Sky) as reliability backstops so a leftover
 *  slot is filled by a feed that is actually live even when the Canadian
 *  networks aren't broadcasting live at the moment. Resolved to each outlet's
 *  current live by the same `NewsMedia` path a pinned news feed uses; an outlet
 *  not currently live shows the player's honest "not live" state (never blank).
 *  Built from the NEWS_SOURCES registry so the slugs/labels stay in lockstep. */
export const WALL_NEWS_FALLBACK: WallNewsFallbackItem[] = [
  'cbc-news',
  'ctv-news',
  'global-news',
  'al-jazeera-english',
  'france24-english',
  'dw-english',
  'sky-news',
]
  .map((id) => findNewsSource(id))
  .filter((s): s is NonNullable<typeof s> => s != null)
  .map((s) => ({ id: s.id, label: s.label }));

/** The news-fallback set a media bank should pass to `selectWallFeeds`, given the
 *  wall's news policy (#371): a wall that allows news (PEOC) auto-rotates the
 *  curated set into empty tiles; a wall that does not (GOC — its own news TVs)
 *  passes an EMPTY set, so empty tiles fall through to the picture/dead camera
 *  tiers (or an honest blank) and never seat a news tile. */
export function newsFallbackFor(allowsNews: boolean): WallNewsFallbackItem[] {
  return allowsNews ? WALL_NEWS_FALLBACK : [];
}
